/// <amd-module name="Wit/Common/SprintEditorDialog/IterationDateUtils" />

import { daysBetweenDates, shiftToLocal, shiftToUTC } from "Wit/Common/DateUtils";

export const enum DayOfWeek {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6
}

const daysOfWeek = [
    DayOfWeek.Sunday,
    DayOfWeek.Monday,
    DayOfWeek.Tuesday,
    DayOfWeek.Wednesday,
    DayOfWeek.Thursday,
    DayOfWeek.Friday,
    DayOfWeek.Saturday
];

export function getWeekendsFromWorkingDays(workingDays: number[]): DayOfWeek[] {
    return daysOfWeek.filter(d => !workingDays.includes(d));
}

export function getNextWorkingDay(startDate?: Date, weekends?: DayOfWeek[]): Date {
    let nextDate: Date = new Date();
    if (startDate) {
        nextDate = new Date(startDate.getTime());
        do {
            nextDate.setDate(nextDate.getDate() + 1);
        } while (weekends?.includes(nextDate.getDay()));
    }

    return nextDate;
}

export function getIterationDateDefaultInformation(
    startDateLocal: Date,
    finishDateLocal: Date,
    weekends: DayOfWeek[]
): { suggestedStartDate: Date; workingDaysOffset: number } {
    // Iteration dates are stored as 0:00 UTC. In order to test for the current "day of the week"
    // (which will be checking the local day of the week) we shift the date value to get an
    // equivalent local time with the same "date" and "time" (00:00) as the UTC value.
    // Then we do the date calculations (and "weekend" test) before shifting back to an
    // equivalent local time (which is then back to 00:00 UTC).
    let suggestedStartDateUTC = shiftToUTC(finishDateLocal);
    suggestedStartDateUTC = getNextWorkingDay(suggestedStartDateUTC, weekends);

    const originalNumWorkingDays = getNumberOfWorkingDays(shiftToUTC(startDateLocal), shiftToUTC(finishDateLocal), weekends);

    const suggestedEndDateUTC = addWorkingDays(suggestedStartDateUTC, originalNumWorkingDays, weekends);
    const offset = daysBetweenDates(suggestedEndDateUTC, suggestedStartDateUTC, true /* exclusive */);

    return {
        suggestedStartDate: shiftToLocal(suggestedStartDateUTC),
        workingDaysOffset: offset
    };
}

export function getNumberOfWorkingDays(startDate: Date, endDate: Date, weekends: DayOfWeek[]): number {
    let numWorkingDays = 0;
    const nextDate = new Date(startDate.getTime());

    while (nextDate <= endDate) {
        // Only count day if it is a working day (not weekend) and the team is working (not a team day off)
        if (!weekends.includes(nextDate.getDay())) {
            numWorkingDays += 1;
        }
        nextDate.setDate(nextDate.getDate() + 1);
    }

    return numWorkingDays;
}

export function addWorkingDays(startDate: Date, numWorkingDays: number, weekends: DayOfWeek[]): Date {
    let remainingWorkingDays = numWorkingDays;
    const endDate = new Date(startDate.getTime());

    while (remainingWorkingDays > 0) {
        if (!weekends.includes(endDate.getDay())) {
            remainingWorkingDays -= 1;
        }
        if (remainingWorkingDays > 0) {
            endDate.setDate(endDate.getDate() + 1);
        }
    }

    return endDate;
}
